/* eslint-disable prettier/prettier  */
// React
import React from "react";
import PropTypes from "prop-types";

// Components
import {
  SEGMENT as TEXT_SEGMENT,
  getNormalizedSegment,
} from "@gcloud-npm/utils.enums.segments";
import getColor, {
  COLOR_DICTIONARY as TEXT_COLOR,
  // OPACITY as TEXT_OPACITY,
} from "@gcloud-npm/utils.enums.colors";

// Utils
import { extendTextInfoClassNames, getProperKind } from "./utils";

// Enums
import {
  TEXT_ALIGN,
  TEXT_BORDER,
  TEXT_DECORATION,
  TEXT_DISPLAY,
  TEXT_WRAP,
  TEXT_FONT_WEIGHT,
  TEXT_HEADING_LEVEL,
  TEXT_KIND,
  TEXT_SIZE,
} from "./enums";

export {
  TEXT_ALIGN,
  TEXT_BORDER,
  TEXT_COLOR,
  // TEXT_OPACITY,
  TEXT_DECORATION,
  TEXT_DISPLAY,
  TEXT_WRAP,
  TEXT_FONT_WEIGHT,
  TEXT_HEADING_LEVEL,
  TEXT_KIND,
  TEXT_SEGMENT,
  TEXT_SIZE,
};

const Text = ({ component, ...restProps }) => {
  const Component = component;
  return <Component {...restProps} />;
};

const COMPONENT_NAME = "TextInfo";
/**
 * Componente que se utiliza para renderizar texto con distintos estilos
 */
const TextInfo = (props) => {
  const {
    className = "",
    segment: segmentProp,
    kind,
    text,
    color,
    opacity = "FF",
    bgOpacity = "FF",
    backgroundColor,
    level,
    cms,
    dotNotation,
    cmsHandler = null,
    cyData,
    decoration,
    display,
    sizeLegacy,
    title,
    onMouseEnter,
    onMouseOver,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    ...restProps
  } = props;

  const normalizedKind = getProperKind(kind, text);

  // smthng
  if (!normalizedKind) {
    return null;
  }

  const segment = getNormalizedSegment(segmentProp);
  const colorOpacity = getColor(color, opacity, segment);
  // console.log("colorOpacity", colorOpacity);
  const bgColorOpacity =
    backgroundColor && backgroundColor !== "transparent"
      ? getColor(backgroundColor, bgOpacity, segment)
      : "transparent";
  const extendedClassNames = extendTextInfoClassNames({
    segment,
    kind: normalizedKind,
    decoration,
    sizeLegacy,
    ...restProps,
  });

  const displayClassName = `text-display--${display}`;

  const componentClassName = `text${
    className ? ` ${className}` : ""
  } text-color--${segment}--${color.toLowerCase()}${extendedClassNames}${
    cmsHandler ? " element-editable" : ""
  } ${displayClassName}`;

  // cms placeholder
  if ((cms || dotNotation) && text === "") {
    if (cmsHandler) {
      return (
        <Text
          component="span"
          className={`${componentClassName} cms-text-placeholder`}
          onClick={
            (cms || dotNotation) && cmsHandler
              ? () =>
                  cmsHandler({
                    ...props,
                    name: "text",
                  })
              : null
          }
          dangerouslySetInnerHTML={{ __html: "Texto" }}
        />
      );
    } else return null;
  }

  return (
    <>
      <Text
        component={
          normalizedKind === TEXT_KIND.HEADING
            ? `${normalizedKind}${level}`
            : normalizedKind
        }
        title={title}
        className={componentClassName}
        dangerouslySetInnerHTML={{ __html: text }}
        onClick={
          (cms || dotNotation) && cmsHandler
            ? () =>
                cmsHandler({
                  ...props,
                  name: "text",
                })
            : null
        }
        data-cy={cyData}
        style={{ color: colorOpacity, backgroundColor: bgColorOpacity }}
        onMouseEnter={onMouseEnter}
        onMouseOver={onMouseOver}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      />
      <style jsx global>{`
// 1) Variables SCSS basadas en nuevos tamaños de marca
$tp-10-fs: clamp(48px, calc(24px + 3.125vw), 56px);
$tp-09-fs: clamp(40px, calc(16px + 3.125vw), 48px);
$tp-08-fs: clamp(32px, calc( 8px + 3.125vw), 40px);
$tp-07-fs: clamp(28px, calc(16px + 1.5625vw), 32px);
$tp-06-fs: clamp(24px, calc(12px + 1.5625vw), 28px);
$tp-05-fs: clamp(20px, calc( 8px + 1.5625vw), 24px);
$tp-04-fs: clamp(18px, calc(12px + 0.78125vw), 20px);
$tp-03-fs: clamp(16px, calc(10px + 0.78125vw), 18px);
$tp-02-fs: clamp(14px, calc( 8px + 0.78125vw), 16px);
$tp-01-fs: clamp(12px, calc( 6px + 1.5625vw), 14px);

$tp-10-lh: clamp(56px, calc(32px + 3.125vw), 64px);
$tp-09-lh: clamp(48px, calc(24px + 3.125vw), 56px);
$tp-08-lh: clamp(38px, calc(8px + 3.90625vw), 48px);
$tp-07-lh: clamp(32px, calc(14px + 2.34375vw), 38px);
$tp-06-lh: clamp(28px, calc(16px + 1.5625vw), 32px);
$tp-05-lh: clamp(26px, calc(20px + 0.78125vw), 28px);
$tp-04-lh: clamp(24px, calc(18px + 0.78125vw), 26px);
$tp-03-lh: clamp(22px, calc(16px + 0.78125vw), 24px);
$tp-02-lh: clamp(20px, calc(14px + 0.78125vw), 22px);
$tp-01-lh: clamp(16px, calc(4px + 1.5625vw), 20px);
  

.text {
  &-size, &-size_legacy {
      // MAPEOS
      &--t1, &--title01 {  font-size: $tp-07-fs; line-height: $tp-07-lh; } // ≈ preset-07
      &--t2, &--title02, &--title015 {  font-size: $tp-07-fs; line-height: $tp-07-lh; } 
      &--t3, &--title03 {  font-size: $tp-06-fs; line-height: $tp-06-lh; } // ≈ preset-06
      &--t4, &--title04 { font-size: $tp-05-fs; line-height: $tp-05-lh; } // ≈ preset-05
      &--t5, &--title045 {  font-size: $tp-05-fs; line-height: $tp-05-lh; } // ≈ preset-05
      &--t6, &--title05 {  font-size: $tp-04-fs; line-height: $tp-04-lh; } // ≈ preset-04
      &--t7, &--title035, &--title06 { font-size: $tp-04-fs; line-height: $tp-04-lh; } // ≈ preset-04
      &--body1,  &--caption01 {  font-size: $tp-03-fs; line-height: $tp-03-lh; } // ≈ preset-03
      &--body2, &--caption02 {  font-size: $tp-02-fs; line-height: $tp-02-lh; } // ≈ preset-02
      &--subtitle1 {  font-size: $tp-03-fs; line-height: $tp-03-lh;  } // ≈ preset-03
      &--subtitle2 { font-size: $tp-02-fs; line-height: $tp-02-lh; }  // ≈ preset-02
      &--caption, &--caption02, &--caption03 {  font-size: $tp-02-fs; line-height: $tp-02-lh; }// ≈ preset-02
      // NUEVO
      &--text-preset-10 { font-size: $tp-10-fs; line-height: $tp-10-lh }
      &--text-preset-09 { font-size: $tp-09-fs; line-height: $tp-09-lh }
      &--text-preset-08 { font-size: $tp-08-fs; line-height: $tp-08-lh }
      &--text-preset-07 { font-size: $tp-07-fs; line-height: $tp-07-lh }
      &--text-preset-06 { font-size: $tp-06-fs; line-height: $tp-06-lh }
      &--text-preset-05 { font-size: $tp-05-fs; line-height: $tp-05-lh }
      &--text-preset-04 { font-size: $tp-04-fs; line-height: $tp-04-lh }
      &--text-preset-03 { font-size: $tp-03-fs; line-height: $tp-03-lh }
      &--text-preset-02 { font-size: $tp-02-fs; line-height: $tp-02-lh }
      &--text-preset-01 { font-size: $tp-01-fs; line-height: $tp-01-lh }
  }

  &-size--price {
    &-xxl {
      font-size: clamp(2.5rem, 12.5vw, 3.125rem);

      @media screen and (min-width: 768px) {
        font-size: clamp(3rem, 6.22vw, 3.5rem);
      }

      @media screen and (min-width: 1024px) {
        font-size: clamp(3rem, 4.66vw, 3.5rem);
      }
    }

    &-xl {
      font-size: 2.25rem;

      @media screen and (min-width: 768px) {
        font-size: clamp(2rem, 3.51vw, 2.25rem);
      }

      @media screen and (min-width: 1024px) {
        font-size: clamp(2rem, 3.166vw, 2.375rem);
      }
    }

    &-l,
    &-m_l {
      font-size: clamp(1.75rem, 9.14vw, 2rem);

      @media screen and (min-width: 768px) {
        font-size: 1.875rem;
      }

      @media screen and (min-width: 1024px) {
      }
    }

    &-m {
      font-size: 1.75rem;
    }

    &-s {
      font-size: 1.5rem;

      @media screen and (min-width: 768px) {
        font-size: 1.625rem;
      }

      @media screen and (min-width: 1024px) {
        font-size: 1.625rem;
      }
    }

    &-xs {
      font-size: 1.125rem;
    }

    &-xxs {
      font-size: 1rem;
    }

    &-xxxs {
      font-size: 0.875rem;
    }
  }

  a{
    color: #0079BB
  }
}

.text {
  overflow-wrap: break-word;
  text-wrap: pretty;


  &-gp {
    /* FONTS */
    &--font-regular {
      font-family: Regular;
      font-weight: 400;
    }
    &--font-light {
      font-family: Light;
      font-weight: 300;
    }
    &--font-bold {
      font-family: Bold;
      font-weight: 400;
    }
    &--font-thin {
      font-family: Thin;
      font-weight: 200;
    }
    &--font-hand {
      font-family: Hand;
    }
  }
  &-emp {
    &--font-regular {
      font-family: RegularEmp;
      font-weight: 400;
    }
    &--font-light {
      font-family: LightEmp;
      font-weight: 300;
    }
    &--font-bold {
      font-family: BoldEmp;
      font-weight: 400;
    }
    &--font-thin {
      font-family: ThinEmp;
      font-weight: 200;
    }
    &--font-hand {
      font-family: HandEmp;
    }
  }
  &-plus {
    &--font-regular {
      font-family: RegularPlus;
      font-weight: 500;
    }
    &--font-light {
      font-family: LightPlus;
      font-weight: 500;
    }
    &--font-bold {
      font-family: BoldPlus;
      font-weight: 500;
    }
  }

  /* ALIGNMENTS */
  &-align {
    &--center {
      text-align: center;
    }
    &--left {
      text-align: left;
    }
    &--right {
      text-align: right;
    }
    &--justify {
      text-align: justify;
    }
  }

  /* DISPLAY */
  &-display {
    &--inline {
      display: inline;
    }
    &--block {
      display: block;
    }
    &--none {
      display: none;
    }
  }

  /* WRAP */
  &-wrap {
    &--pretty {
      text-wrap: pretty;
    }
    &--balance {
      text-wrap: balance;
    }
    &--wrap {
      text-wrap: wrap;
    }
    &--nowrap {
      text-wrap: nowrap;
    }
  }

  /* BORDER */
  &-border--rounded {
    border-radius: 3.125rem;
    padding: 0.25rem 0.75rem;
  }

  /* DECORATION */
  &-decoration {
    &--underline {
      text-decoration: underline;
      text-underline-offset: 4px;
    }
    &--line-through {
      text-decoration: line-through;
    }
  }
  * {
    line-height: 140%;
  }
  ol {
    list-style: auto;
    li {
      position: relative;
      left: 1.25rem;
      padding-right: 1.25rem;
    }
    li[data-list="bullet"]{
      list-style: disc;
    }

  }
  ul{
    list-style: inside;
    li{
      position: relative;
    }
    li span {
      position: relative;
    }
  }


}
`}</style>
    </>
  );
};

TextInfo.propTypes = {
  /**
   * {
   *  "info": "Texto que se mostrará en el componente. Puede guardar haciendo control+s",
   *  "kind": "both",
   *  "beautifulName": "Texto"
   * }
   */
  text: PropTypes.string.isRequired,

  /**
   * {
   *  "info": "Texto que se mostrará al dejar el cursor sobre el componente (tooltip/title).",
   *  "kind": "both",
   *  "beautifulName": "Título"
   * }
   */
  title: PropTypes.string,

  /**
   * {
   *  "info": "Tipo de texto, define si el componente es un heading, paragraph o span.",
   *  "kind": "dev",
   *  "beautifulName": "Tipo",
   *  "values": "./enums.js:TEXT_KIND"
   * }
   */
  kind: PropTypes.string,

  /**
   * {
   *  "info": "Nivel del heading (h1, h2, h3, etc.), si el componente es un heading.",
   *  "kind": "dev",
   *  "beautifulName": "Nivel de Header",
   *  "values": "./enums.js:TEXT_HEADING_LEVEL"
   * }
   */
  level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * {
   *  "info": "Clase CSS adicional para aplicar estilos personalizados.",
   *  "kind": "dev",
   *  "beautifulName": "ClassName"
   * }
   */
  className: PropTypes.string,

  /**
   * {
   *  "info": "Color del texto.",
   *  "kind": "both",
   *  "beautifulName": "Color"
   * }
   */
  color: PropTypes.string,

  /**
   * {
   *  "info": "Tamaño del texto, puede ser 'Title', 'Display' o 'Caption' (cada uno con sus niveles).",
   *  "kind": "both",
   *  "beautifulName": "Tamaño",
   *  "values": "./enums.js:TEXT_SIZE_ENUMS"
   * }
   */
  size: PropTypes.string,

  /**
   * {
   *  "info": "Fuente del texto, puede ser 'font-regular', 'font-light' o 'font-bold'.",
   *  "kind": "both",
   *  "beautifulName": "Fuente",
   *  "values": "./enums.js:TEXT_FONT_WEIGHT"
   * }
   */
  fontType: PropTypes.string,

  /**
   * {
   *  "info": "Alineación del texto: 'center', 'left', 'right', 'justify'.",
   *  "kind": "both",
   *  "beautifulName": "Alineación",
   *  "values": "./enums.js:TEXT_ALIGN"
   * }
   */
  align: PropTypes.string,

  /**
   * {
   *  "info": "Especifica si el texto se renderiza como block o inline.",
   *  "kind": "dev",
   *  "beautifulName": "Display",
   *  "values": "./enums.js:TEXT_DISPLAY"
   * }
   */
  display: PropTypes.string,

  /**
   * {
   *  "info": "Controla el comportamiento del wrapping del texto. Valores posibles: 'pretty', 'balance', 'wrap', 'nowrap'.",
   *  "kind": "both",
   *  "beautifulName": "Wrap",
   *  "values": "./enums.js:TEXT_WRAP"
   * }
   */
  wrap: PropTypes.string,

  /**
   * {
   *  "info": "Decoración del texto, como subrayado o línea cruzada.",
   *  "kind": "dev",
   *  "beautifulName": "Decoración",
   *  "values": "./enums.js:TEXT_DECORATION"
   * }
   */
  decoration: PropTypes.string,

  /**
   * {
   *  "info": "Borde del texto.",
   *  "kind": "dev",
   *  "beautifulName": "Borde",
   *  "values": "./enums.js:TEXT_BORDER"
   * }
   */
  border: PropTypes.string,

  /**
   * {
   *  "info": "Color de fondo del texto.",
   *  "kind": "dev",
   *  "beautifulName": "Color del Fondo"
   * }
   */
  backgroundColor: PropTypes.string,
  /**
   * {
   *  "info": "Objeto con información sobre las propiedades necesarias (id, timestamp...) para editar los valores en orion.",
   *  "kind": "dev",
   *  "beautifulName": "CMS"
   * }
   */ cms: PropTypes.object,
  /**
   * {
   *  "info": "Identificador para edición en astro maker",
   *  "kind": "dev",
   *  "beautifulName": "CMS"
   * }
   */ dotNotation: PropTypes.string,
  /**
   * {
   *  "info": "Opacidad del texto.",
   *  "kind": "dev",
   *  "beautifulName": "Opacidad"
   * }
   */
  opacity: PropTypes.string,

  /**
   * {
   *  "info": "Opacidad del fondo del texto.",
   *  "kind": "dev",
   *  "beautifulName": "Opacidad del fondo"
   * }
   */
  bgOpacity: PropTypes.string,
  /**
   * {
   *  "info": "Datos de prueba para Cypress.",
   *  "kind": "dev",
   *  "beautifulName": "cyData"
   * }
   */
  cyData: PropTypes.string,

  /**
   * {
   *  "info": "Activa el uso de tamaños legados para el texto.",
   *  "kind": "dev",
   *  "beautifulName": "sizeLegacy"
   * }
   */
  sizeLegacy: PropTypes.bool,

  /**
   * {
   *  "info": "Synthetic event onMouseEnter.",
   *  "kind": "dev",
   *  "beautifulName": "onMouseEnter"
   * }
   */
  onMouseEnter: PropTypes.func,

  /**
   * {
   *  "info": "Synthetic event onMouseOver.",
   *  "kind": "dev",
   *  "beautifulName": "onMouseOver"
   * }
   */
  onMouseOver: PropTypes.func,

  /**
   * {
   *  "info": "Synthetic event onMouseDown.",
   *  "kind": "dev",
   *  "beautifulName": "onMouseDown"
   * }
   */
  onMouseDown: PropTypes.func,

  /**
   * {
   *  "info": "Synthetic event onMouseUp.",
   *  "kind": "dev",
   *  "beautifulName": "onMouseUp"
   * }
   */
  onMouseUp: PropTypes.func,

  /**
   * {
   *  "info": "Synthetic event onMouseLeave.",
   *  "kind": "dev",
   *  "beautifulName": "onMouseLeave"
   * }
   */
  onMouseLeave: PropTypes.func,
};

TextInfo.defaultProps = {
  title: "",
  kind: TEXT_KIND.SPAN,
  level: TEXT_HEADING_LEVEL.H1,
  className: "",
  color: TEXT_COLOR.MOVISTAR_BLACK,
  size: TEXT_SIZE.BODY2,
  fontType: TEXT_FONT_WEIGHT.REGULAR,
  cms: null,
  dotNotation: null,
  align: TEXT_ALIGN.LEFT,
  border: TEXT_BORDER.NONE,
  decoration: TEXT_DECORATION.NONE,
  display: TEXT_DISPLAY.BLOCK,
  wrap: TEXT_WRAP.PRETTY,
  backgroundColor: TEXT_COLOR.TRANSPARENT,
  // opacity: TEXT_OPACITY[100],
  // bgOpacity: TEXT_OPACITY[100],
  sizeLegacy: false,
  cyData: "",
};

export default TextInfo;
